import { Action } from "@packages/shared-types";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Loader2, Paperclip, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	getFinancialTransactionQueryOptions,
	listFinancialCategoriesQueryOptions,
	useDeleteManualEntry,
	useReviewFinancialTransactions,
	useUpdateManualEntry,
} from "@/api/financial";
import { ImageUploadDialog } from "@/components/media/ImageUploadDialog";
import { MediaGallery } from "@/components/media/MediaGallery";
import { PageHeader } from "@/components/PageHeader";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { formatSignedMoney } from "@/utils/financial";
import {
	KindBadge,
	ReviewBadge,
	SourceBadge,
} from "../../-components/FinancialBadges";
import {
	ManualEntryForm,
	type ManualEntryFormValues,
} from "../../-components/ManualEntryForm";
import { SourceLink } from "../../-components/sourceLink";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/transactions/$transactionId/",
)({
	loader: async ({ context, params }) => {
		await context.queryClient.ensureQueryData(
			getFinancialTransactionQueryOptions(params.orgId, params.transactionId),
		);
	},
	component: TransactionDetailPage,
});

function TransactionDetailPage() {
	const { orgId, transactionId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const { formatDateOnly, formatForDateInput } = useDateFormatter();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);
	const [isEditing, setIsEditing] = useState(false);
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);

	const { data: transaction } = useSuspenseQuery(
		getFinancialTransactionQueryOptions(orgId, transactionId),
	);
	const { data: categories } = useQuery(
		listFinancialCategoriesQueryOptions(orgId),
	);

	const reviewMutation = useReviewFinancialTransactions();
	const updateMutation = useUpdateManualEntry();
	const deleteMutation = useDeleteManualEntry();

	const isManual = transaction.entryOrigin === "MANUAL";
	const activeCategories = (categories ?? []).filter((c) => c.isActive);

	const goToList = () =>
		navigate({
			to: "/app/org/$orgId/financial/transactions",
			params: { orgId },
		});

	const handleReview = (categoryId?: string) =>
		reviewMutation.mutate({
			organizationId: orgId,
			data: {
				transactionIds: [transactionId],
				reviewState: "REVIEWED",
				...(categoryId ? { categoryId } : {}),
			},
		});

	const confirmDelete = () =>
		deleteMutation.mutate(
			{ organizationId: orgId, transactionId },
			{ onSuccess: goToList },
		);

	if (isEditing && isManual) {
		// eventDate is a UTC-midnight date-only value; build the <input type="date">
		// value with the locale-independent UTC helper so a UTC-negative locale (all
		// of Brazil) doesn't pre-fill — and silently persist — the prior day (BUG-6).
		const dateValue = formatForDateInput(
			new Date(transaction.eventDate).getTime(),
		);
		const defaults: Partial<ManualEntryFormValues> = {
			direction: transaction.direction,
			amount: transaction.amount,
			eventDate: dateValue,
			categoryId: transaction.categoryId,
			establishmentId: transaction.establishmentId ?? undefined,
			vendorId: transaction.vendorId ?? "",
			party: transaction.party ?? "",
			notes: transaction.notes ?? "",
		};
		return (
			<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
				<PageHeader
					title={t("financial.transactions.manualEntry.editTitle")}
					showBackButton
				/>
				<div className="card bg-base-100 shadow-md">
					<div className="card-body">
						<ManualEntryForm
							orgId={orgId}
							defaultValues={defaults}
							isSubmitting={updateMutation.isPending}
							onSubmit={(data) =>
								updateMutation
									.mutateAsync({ organizationId: orgId, transactionId, data })
									.then(() => setIsEditing(false))
							}
							onCancel={() => setIsEditing(false)}
						/>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
			<PageHeader
				title={t("financial.transactions.detail.title")}
				showBackButton
			/>

			<Card>
				<CardContent className="flex flex-col gap-4 p-5">
					<div className="flex items-start justify-between gap-3">
						<div className="flex flex-wrap items-center gap-2">
							<KindBadge kind={transaction.kind} />
							<SourceBadge
								sourceModule={transaction.sourceModule}
								entryOrigin={transaction.entryOrigin}
							/>
							<ReviewBadge reviewState={transaction.reviewState} />
							{transaction.status === "VOID" && (
								<Badge variant="outline" className="text-base-content/50">
									VOID
								</Badge>
							)}
						</div>
						<span
							className={
								transaction.direction === "OUT"
									? "text-xl font-semibold text-error"
									: "text-xl font-semibold text-success"
							}
						>
							{formatSignedMoney(
								transaction.amount,
								transaction.direction,
								transaction.currency,
							)}
						</span>
					</div>

					<DetailRow
						label={t("financial.transactions.columns.date")}
						value={formatDateOnly(new Date(transaction.eventDate).getTime())}
					/>
					<DetailRow
						label={t("financial.transactions.columns.category")}
						value={transaction.categoryLabel}
					/>
					{transaction.sourceDescription && (
						<DetailRow
							label={t("financial.transactions.columns.description")}
							value={transaction.sourceDescription}
						/>
					)}
					{transaction.party && (
						<DetailRow
							label={t("financial.transactions.detail.party")}
							value={transaction.party}
						/>
					)}
					{transaction.notes && (
						<DetailRow
							label={t("financial.transactions.detail.notes")}
							value={transaction.notes}
						/>
					)}

					{transaction.sourceLink ? (
						<div className="flex items-center gap-2 rounded-lg bg-base-200 px-3 py-2 text-sm text-base-content/80">
							<SourceLink
								orgId={orgId}
								source={{
									sourceModule: transaction.sourceModule,
									sourceType: transaction.sourceType,
									sourceId: transaction.sourceId,
									dimensions: transaction.dimensions,
									establishmentId: transaction.establishmentId,
								}}
								label={transaction.sourceLink.label}
							/>
						</div>
					) : (
						<p className="text-sm text-base-content/60">
							{t("financial.transactions.detail.noSource")}
						</p>
					)}

					{!isManual && (
						<p className="text-xs text-base-content/60">
							{t("financial.transactions.detail.readOnlyNotice")}
						</p>
					)}
				</CardContent>
			</Card>

			{canManage && transaction.status !== "VOID" && (
				<Card>
					<CardContent className="flex flex-col gap-4 p-5">
						{transaction.reviewState === "UNREVIEWED" && (
							<div className="space-y-2">
								<Label>
									{t("financial.transactions.review.changeCategory")}
								</Label>
								<Select
									value={transaction.categoryId}
									onValueChange={(v) => handleReview(v)}
								>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{activeCategories.map((category) => (
											<SelectItem key={category.id} value={category.id}>
												{category.label} ·{" "}
												{t(`financial.kinds.${category.kind}`)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
						<div className="flex flex-wrap gap-2">
							{transaction.reviewState === "UNREVIEWED" && (
								<Button
									onClick={() => handleReview()}
									disabled={reviewMutation.isPending}
								>
									<Check className="mr-2 h-4 w-4" />
									{t("financial.transactions.review.markReviewed")}
								</Button>
							)}
							{isManual && (
								<>
									<Button variant="outline" onClick={() => setIsEditing(true)}>
										<Pencil className="mr-2 h-4 w-4" />
										{t("common.edit")}
									</Button>
									<Button
										variant="outline"
										className="text-error"
										onClick={() => setIsDeleteOpen(true)}
										disabled={deleteMutation.isPending}
									>
										<Trash2 className="mr-2 h-4 w-4" />
										{t("common.delete")}
									</Button>
								</>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			<Card>
				<CardContent className="p-5">
					<MediaGallery
						orgId={orgId}
						entityType="FinancialHubTransaction"
						entityId={transactionId}
						title={t("financial.transactions.attachments.title")}
						emptyText={t("financial.transactions.attachments.empty")}
						headerAction={
							canManage && transaction.status !== "VOID" ? (
								<ImageUploadDialog
									orgId={orgId}
									entityType="FinancialHubTransaction"
									entityId={transactionId}
									title={t("financial.transactions.attachments.uploadTitle")}
									trigger={
										<Button variant="outline" size="sm">
											<Paperclip className="mr-2 h-4 w-4" />
											{t("financial.transactions.attachments.attach")}
										</Button>
									}
								/>
							) : null
						}
					/>
				</CardContent>
			</Card>

			<AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("financial.transactions.delete.title")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("financial.transactions.delete.confirm")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={deleteMutation.isPending}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDelete}
							disabled={deleteMutation.isPending}
						>
							{deleteMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("common.delete")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

function DetailRow({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-start justify-between gap-3 border-b border-base-200 pb-2 text-sm last:border-0">
			<span className="text-base-content/60">{label}</span>
			<span className="text-right font-medium">{value}</span>
		</div>
	);
}
